import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, IconBadge, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { SCHOOL_DEPLOYMENT } from '../../lib/mockSchool';
import { Check, Plus, Calculator, ArrowRight, Clock } from 'lucide-react';

function Estimator({ d }) {
  const [n, setN] = useState('');
  const [result, setResult] = useState('');
  const calc = () => {
    const s = parseInt(n, 10);
    if (!s || s <= 0) { setResult(''); return; }
    const headsets = Math.max(3, Math.ceil(s / 20));
    setResult(`${s} students → ${headsets} headsets`);
  };
  return (
    <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
      <label htmlFor="dep-est" className="text-[13px] font-semibold text-ink-700">{d.inputLabel}</label>
      <div className="mt-3 flex gap-2">
        <input
          id="dep-est"
          type="number"
          min="1"
          value={n}
          onChange={(e) => setN(e.target.value)}
          placeholder="Enter number"
          data-testid="deployment-estimator-input"
          className="flex-1 rounded-lg border border-canvas-200 bg-white px-4 py-2.5 text-[14px] text-navy focus:outline-none focus:ring-2 focus:ring-purple/40"
        />
        <button onClick={calc} data-testid="deployment-estimator-calculate" className="inline-flex items-center gap-1.5 rounded-lg bg-brand-gradient px-4 py-2.5 text-[13.5px] font-semibold text-white shadow-soft hover:brightness-110">
          <Calculator className="h-4 w-4" /> {d.buttonLabel}
        </button>
      </div>
      <div className="mt-5 rounded-xl bg-mint-50 p-4 ring-1 ring-mint/20">
        <div className="text-[11px] font-bold tracking-widest text-mint-600">RECOMMENDED</div>
        <div className="mt-1 text-[20px] font-extrabold text-navy" data-testid="deployment-estimator-result">{result || d.resultExample}</div>
      </div>
      <div className="mt-4">
        <GradientButton to={d.customCta.to} variant="outline" data-testid="deployment-custom-cta">{d.customCta.label} <ArrowRight className="h-4 w-4" /></GradientButton>
      </div>
    </div>
  );
}

export default function SchoolDeployment() {
  const d = SCHOOL_DEPLOYMENT;
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="school-deployment-hero">
        <div className="mx-auto max-w-[1240px]">
          <h1 className="text-[40px] md:text-[54px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
          <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
          <div className="mt-6"><LicenceFeeHighlight /></div>
          <div className="mt-6 flex flex-wrap gap-3">
            <GradientButton to={d.hero.ctaPrimary.to} data-testid="deployment-hero-start">{d.hero.ctaPrimary.label}</GradientButton>
            <GradientButton to={d.hero.ctaSecondary.to} variant="outline" data-testid="deployment-hero-demo">{d.hero.ctaSecondary.label}</GradientButton>
          </div>
        </div>
      </section>

      {/* REQUIREMENTS — Minimum deployment standard */}
      <section className="px-6 py-14" data-testid="deployment-requirements">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.requirements.eyebrow} title={d.requirements.title} desc={d.requirements.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-purple text-white"><Check className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.requirements.required.title}</h3>
              </div>
              <ul className="mt-4 space-y-2.5">
                {d.requirements.required.items.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><Check className="mt-0.5 h-4 w-4 flex-none text-purple" /> {r}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-cyan text-white"><Plus className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.requirements.optional.title}</h3>
              </div>
              <ul className="mt-4 space-y-2.5">
                {d.requirements.optional.items.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink-700"><span className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-cyan" /> {r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ESTIMATOR */}
      <section className="px-6 py-14 bg-white" data-testid="deployment-estimator">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.estimator.eyebrow} title={d.estimator.title} desc={d.estimator.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[1.05fr_1fr]">
            <Estimator d={d.estimator} />
            <div className="rounded-2xl border border-canvas-200 bg-canvas-100 p-6 shadow-soft">
              <div className="text-[15px] font-bold text-navy">Ratio you can plan around</div>
              <ul className="mt-4 space-y-2.5 text-[13.5px] text-ink-700">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-purple" /> Recommended: 1 headset per ~20 students</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-purple" /> Minimum deployment: 3 Meta Quest 3S headsets</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-purple" /> Result rounds up to the nearest whole headset</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ACCESS MODEL — what is included vs funded */}
      <section className="px-6 py-14" data-testid="deployment-access-model">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.accessModel.eyebrow} title={d.accessModel.title} desc={d.accessModel.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-6 shadow-soft ring-1 ring-mint/20">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-mint text-white shadow"><Check className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.accessModel.included.title}</h3>
              </div>
              <p className="mt-4 text-[13.5px] leading-relaxed text-ink-700">{d.accessModel.included.body}</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-royal text-white shadow"><Plus className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.accessModel.funded.title}</h3>
              </div>
              <p className="mt-4 text-[13.5px] leading-relaxed text-ink-700">{d.accessModel.funded.body}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS — Four stages to begin */}
      <section className="px-6 py-14 bg-white" data-testid="deployment-process">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.process.eyebrow} title={d.process.title} desc={d.process.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.process.stages.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-card" data-testid={`deployment-stage-${s.n}`}>
                <div className="flex items-center gap-3">
                  <IconBadge icon={s.icon} color={s.color} size="lg" />
                  <StepChip n={s.n} color={s.color} />
                </div>
                <h3 className="mt-4 text-[15px] font-bold text-navy">{s.n}. {s.title}</h3>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE — Expected access */}
      <section className="px-6 py-14" data-testid="deployment-timeline">
        <div className="mx-auto max-w-[1240px]">
          <div className="rounded-3xl bg-gradient-to-br from-navy via-royal to-cyan p-8 md:p-10 text-white grain relative overflow-hidden">
            <div className="relative z-10 grid grid-cols-1 gap-6 md:grid-cols-[1.15fr_1fr] items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold tracking-widest text-white ring-1 ring-white/20"><Clock className="h-3.5 w-3.5" /> TIMELINE</div>
                <h2 className="mt-3 text-[28px] md:text-[34px] font-extrabold tracking-tight">{d.timeline.title}</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-white/80 max-w-xl">{d.timeline.desc}</p>
              </div>
              <div className="flex md:justify-end">
                <GradientButton to={d.timeline.cta.to} data-testid="deployment-timeline-cta">{d.timeline.cta.label} <ArrowRight className="h-4 w-4" /></GradientButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={d.cta.primary} secondary={d.cta.secondary} />
        </div>
      </section>
    </PageShell>
  );
}
